<template>
  <article id="lotto-wrapper" class="contents-wrapper">
    <div id="lotto-home">
      <!-- 헤더 -->
      <div class="dash-head">
        <div class="dash-head-text">
          <h1 class="dash-title">로또</h1>
          <p class="dash-sub">최신 당첨 결과와 내 구매 현황을 한눈에.</p>
        </div>
        <router-link class="dash-cta" to="/lotto/generate">+ 번호 생성하기</router-link>
      </div>

      <!-- 상단: 최신 결과 + 내 요약 -->
      <div class="dash-top">
        <!-- 당첨 결과 (회차 이동) -->
        <div class="dash-card result-card">
          <div class="rc-head">
            <div class="rc-nav">
              <button class="rc-arrow" :disabled="result.drwNo <= 1" @click="prevDrw" aria-label="이전 회차"></button>
              <div class="rc-round">제 <b>{{ result.drwNo || '-' }}</b>회</div>
              <button class="rc-arrow next" :disabled="result.drwNo >= lastDrwNo" @click="nextDrw" aria-label="다음 회차"></button>
            </div>
            <div class="rc-date" v-if="result.drwNoDate">{{ formatDate(result.drwNoDate) }} 추첨</div>
          </div>

          <div class="rc-balls" v-if="result.drwNo > 0">
            <div
              v-for="(num, i) in winNumbers"
              :key="'rc-' + i"
              class="lotto-ball"
              :class="getColorClass(num)"
            >
              <span class="number">{{ num }}</span>
            </div>
            <span class="rc-plus">+</span>
            <div class="lotto-ball" :class="getColorClass(result.bnusNo)">
              <span class="number">{{ result.bnusNo }}</span>
            </div>
          </div>
          <div class="rc-bonus-label">마지막은 보너스 번호</div>

          <div class="rc-prize-row" v-if="result.drwNo > 0">
            <div class="rc-prize">
              <span class="rc-prize-v accent">{{ getMoneyFormat(result.firstWinamnt) }}원</span>
              <span class="rc-prize-l">1등 1인 당첨금</span>
            </div>
            <div class="rc-prize">
              <span class="rc-prize-v">{{ getMoneyFormat(result.firstPrzwnerCo) }}명</span>
              <span class="rc-prize-l">1등 당첨자</span>
            </div>
            <div class="rc-prize">
              <span class="rc-prize-v">{{ getShortenMoney(result.totSellamnt) }}</span>
              <span class="rc-prize-l">총 판매금액</span>
            </div>
          </div>

          <router-link class="rc-more" :to="{ path: '/lotto/result', query: { drwNo: result.drwNo } }">당첨 결과 자세히 →</router-link>
        </div>

        <!-- 내 로또 요약 -->
        <div class="dash-card summary-card">
          <div class="sc-title">내 로또 요약</div>
          <template v-if="isLogin">
            <div class="sc-row">
              <span class="sc-l">누적 구매</span>
              <span class="sc-v">{{ getMoneyFormat(myStats.totalGames) }} 게임</span>
            </div>
            <div class="sc-row">
              <span class="sc-l">누적 당첨금</span>
              <span class="sc-v accent">{{ getMoneyFormat(myStats.totalPrize) }}원</span>
            </div>
            <div class="sc-row">
              <span class="sc-l">이번 주 구매</span>
              <span class="sc-v">{{ progressList.length }} 게임</span>
            </div>
            <div class="sc-row">
              <span class="sc-l">최고 등수</span>
              <span class="sc-v">{{ myStats.bestRank <= 5 ? myStats.bestRank + '등' : '-' }}</span>
            </div>
          </template>
          <div v-else class="sc-empty">
            <p>로그인하면 내 구매·당첨 현황을 볼 수 있어요.</p>
            <button class="sc-login-btn" @click="goLogin">로그인</button>
          </div>
        </div>
      </div>

      <!-- 브리저 당첨 명예의 전당 (공개) -->
      <div class="dash-hall">
        <!-- 최근 당첨 내역 -->
        <div class="dash-card hall-card">
          <div class="hc-title">브리저 최근 당첨</div>
          <p class="hc-desc">브리저에서 만들어 구매한 번호 중 최근 당첨된 내역이에요.</p>
          <div v-if="recentWins.length > 0" class="hall-list">
            <div v-for="(g, idx) in recentWins" :key="'rw-' + idx" class="hall-item">
              <span class="hall-rank" :class="'rank-' + g.rank">{{ g.rank }}등</span>
              <div class="hall-balls">
                <div
                  v-for="(num, i) in gameNumbers(g)"
                  :key="'rwb-' + idx + '-' + i"
                  class="hall-ball"
                  :class="getColorClass(num)"
                >{{ num }}</div>
              </div>
              <span class="hall-meta">{{ g.drwNo }}회 · {{ g.nickname || '익명' }}</span>
            </div>
          </div>
          <div v-else class="hall-empty">아직 당첨 내역이 없어요.</div>
        </div>

        <!-- 역대 최고 등수 -->
        <div class="dash-card hall-card">
          <div class="hc-title">역대 최고 등수</div>
          <p class="hc-desc">브리저 이용자들이 기록한 역대 최고 순위예요.</p>
          <div v-if="bestWins.length > 0" class="hall-list">
            <div v-for="(g, idx) in bestWins" :key="'bw-' + idx" class="hall-item">
              <span class="hall-rank" :class="'rank-' + g.rank">{{ g.rank }}등</span>
              <div class="hall-balls">
                <div
                  v-for="(num, i) in gameNumbers(g)"
                  :key="'bwb-' + idx + '-' + i"
                  class="hall-ball"
                  :class="getColorClass(num)"
                >{{ num }}</div>
              </div>
              <span class="hall-meta">{{ g.drwNo }}회 · {{ g.nickname || '익명' }}</span>
            </div>
          </div>
          <div v-else class="hall-empty">아직 기록이 없어요.</div>
        </div>
      </div>

      <!-- 이번 주 내 구매 (로그인) -->
      <div class="dash-week" v-if="isLogin">
        <div class="dw-head">
          <h2 class="dw-title">
            이번 주 내 구매
            <span class="dw-meta" v-if="progressDrwNo > 0">· 제 {{ progressDrwNo }}회 (추첨 전)</span>
          </h2>
          <span class="dw-legend" v-if="progressList.length > 0">유형별로 색이 구분돼요</span>
        </div>

        <div v-if="progressList.length > 0" class="dw-list">
          <div v-for="(game, idx) in progressList" :key="'dw-' + idx" class="dw-item">
            <LottoBuyGame :lottoGame="game" />
          </div>
        </div>
        <div v-else class="dw-empty">
          <div class="dw-empty-icon">🎱</div>
          <p>이번 주 구매한 번호가 없어요.</p>
          <router-link class="dw-empty-cta" to="/lotto/generate">번호 생성하러 가기</router-link>
        </div>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { LottoGameBuyResponse, LottoResultResponse } from "../class";
import LottoBuyGame from "../components/LottoBuyGame.vue";

export default defineComponent({
  name: "LottoHomeView",
  components: { LottoBuyGame },
  mixins: [UtilMixin],
  data() {
    return {
      result: new LottoResultResponse(),
      lastDrwNo: 0,
      statsGames: [] as LottoGameBuyResponse[],
      progressList: [] as LottoGameBuyResponse[],
      progressDrwNo: 0,
      recentWins: [] as LottoGameBuyResponse[],
      bestWins: [] as LottoGameBuyResponse[],
    };
  },
  computed: {
    isLogin(): boolean {
      return this.getCore().isLogin();
    },
    winNumbers(): number[] {
      const r = this.result;
      return [r.drwtNo1, r.drwtNo2, r.drwtNo3, r.drwtNo4, r.drwtNo5, r.drwtNo6];
    },
    myStats(): { totalGames: number; totalPrize: number; bestRank: number } {
      const all = [...this.progressList, ...this.statsGames];
      const drawn = all.filter((g) => g.isDraw());
      const totalPrize = drawn.reduce((s, g) => s + g.prize, 0);
      const bestRank = drawn.length > 0 ? Math.min(...drawn.map((g) => g.rank)) : 99;
      return { totalGames: all.length, totalPrize, bestRank };
    },
  },
  methods: {
    getColorClass(number: number): string {
      if (number >= 1 && number <= 9) return "bg-yellow-400";
      if (number >= 10 && number <= 19) return "bg-blue-400";
      if (number >= 20 && number <= 29) return "bg-red-400";
      if (number >= 30 && number <= 39) return "bg-gray-400";
      if (number >= 40 && number <= 45) return "bg-green-400";
      return "";
    },
    gameNumbers(g: LottoGameBuyResponse): number[] {
      return [g.drwtNo1, g.drwtNo2, g.drwtNo3, g.drwtNo4, g.drwtNo5, g.drwtNo6];
    },
    formatDate(dateStr: string): string {
      if (!dateStr) return "";
      const m = dateStr.match(/(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/);
      if (!m) return dateStr;
      return `${m[1]}.${m[2].padStart(2, "0")}.${m[3].padStart(2, "0")}`;
    },
    getShortenMoney(amount: number): string {
      if (amount >= 100000000) {
        const eok = Math.floor(amount / 100000000);
        const rest = Math.round((amount % 100000000) / 10000000);
        return rest > 0 ? `${eok}.${rest}억` : `${eok}억`;
      } else if (amount >= 10000) {
        return `${Math.round(amount / 10000)}만`;
      }
      return this.getMoneyFormat(amount);
    },
    goLogin() {
      this.getRouterUtil().moveTo("/user/login");
    },
    async fetchResult(drwNo?: number) {
      try {
        if (drwNo === undefined) {
          const res = await this.getApi().lotto().getLastLottoResult();
          this.result = res;
          this.lastDrwNo = res.drwNo;
        } else {
          const res = await this.getApi().lotto().getLottoResult(drwNo, false);
          if (res && res.drwNo > 0) this.result = res;
        }
      } catch (e) {
        console.error("fetchResult error:", e);
      }
    },
    prevDrw() {
      if (this.result.drwNo > 1) this.fetchResult(this.result.drwNo - 1);
    },
    nextDrw() {
      if (this.result.drwNo < this.lastDrwNo) this.fetchResult(this.result.drwNo + 1);
    },
    async fetchWinGames() {
      // 공개 API: 브리저에서 구매한 당첨 게임
      try {
        const res = await this.getApi().lotto().getWinGameList(1, 100);
        if (res && Array.isArray(res.content)) {
          const games = res.content.map((item: any) => {
            const g = new LottoGameBuyResponse();
            g.init(item);
            return g;
          });
          // 최근 당첨 (API가 createDatetime DESC 정렬) 상위 5
          this.recentWins = games.slice(0, 5);
          // 역대 최고 등수: rank 오름차순 → 같으면 최근순
          this.bestWins = [...games]
            .sort((a, b) => (a.rank - b.rank) || (b.drwNo - a.drwNo))
            .slice(0, 5);
        }
      } catch (e) {
        console.error("fetchWinGames error:", e);
      }
    },
    async fetchMyData() {
      if (!this.isLogin) return;
      try {
        const summary = await this.getApi().lotto().getLottoGameBuySummary();
        this.progressDrwNo = summary.drwNo;
        this.progressList = summary.progressList;
      } catch (e) {
        console.error("fetchMyData summary error:", e);
      }
      try {
        const loginInfo = this.getCore().getLoginInfo();
        if (loginInfo && loginInfo.id) {
          const res = await this.getApi().lotto().getLottoGameBuyList(loginInfo.id, 1, 100000);
          if (res && Array.isArray(res.content)) {
            this.statsGames = res.content.map((item: any) => {
              const g = new LottoGameBuyResponse();
              g.init(item);
              return g;
            });
          }
        }
      } catch (e) {
        console.error("fetchMyData list error:", e);
      }
    },
  },
  created() {
    window.scrollTo(0, 0);
    this.fetchResult();
    this.fetchWinGames();
    this.fetchMyData();
  },
});
</script>

<style lang="scss" scoped></style>
